import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Search } from 'lucide-react';
import Logo from './Logo';
import { NAV_LINKS } from '../mock';

export default function Header() {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setSearchOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-white/90 backdrop-blur-md shadow-sm py-1 border-b border-slate-100' : 'bg-white/70 backdrop-blur-md py-3'
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 flex items-center justify-between">
        <Link to="/" className="flex items-center shrink-0">
          <Logo />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`relative text-[13px] font-semibold uppercase tracking-wide font-heading px-3.5 py-2 rounded-full transition-colors duration-200 ${
                isActive(link.path)
                  ? 'text-[#1ca5dc] bg-[#eaf6fc]'
                  : 'text-[#132b4d] hover:text-[#1ca5dc] hover:bg-slate-50'
              }`}
            >
              {link.label}
            </Link>
          ))}
          <button
            onClick={() => setSearchOpen((s) => !s)}
            className="ml-2 w-9 h-9 flex items-center justify-center rounded-full bg-[#1ca5dc] text-white hover:bg-[#132b4d] transition-colors"
            aria-label="Search"
          >
            <Search size={16} />
          </button>
        </nav>

        {/* Mobile toggles */}
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={() => setSearchOpen((s) => !s)}
            className="w-9 h-9 flex items-center justify-center rounded-full bg-[#1ca5dc] text-white"
            aria-label="Search"
          >
            <Search size={16} />
          </button>
          <button
            onClick={() => setOpen((o) => !o)}
            className="w-10 h-10 flex items-center justify-center text-[#132b4d]"
            aria-label="Menu"
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {/* Search bar */}
      {searchOpen && (
        <div className="border-t border-gray-100 bg-white">
          <div className="max-w-7xl mx-auto px-5 lg:px-8 py-3 flex items-center gap-3">
            <Search size={18} className="text-gray-400" />
            <input
              autoFocus
              type="text"
              placeholder="Search anything…"
              className="flex-1 outline-none text-sm py-1 bg-transparent"
            />
          </div>
        </div>
      )}

      {/* Mobile menu */}
      {open && (
        <nav className="lg:hidden bg-white border-t border-gray-100 shadow-lg">
          <div className="px-5 py-2 flex flex-col">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`py-3 text-sm font-semibold uppercase tracking-wide font-heading border-b border-gray-50 ${
                  isActive(link.path) ? 'text-[#1ca5dc]' : 'text-[#132b4d]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </div>
        </nav>
      )}
    </header>
  );
}
